/**
 * Kept free of react-admin and DOM imports so it can be unit-tested under jest.
 */

import { UserInterface } from '@vatos-pas/common';
import { formatDateTime } from './formatDateTime';

export const COST_STRUCTURE_HISTORY_TIMESTAMP_FORMAT = 'MM/dd/yyyy, hh:mm a';

export type CsvCellValue = string | number | null | undefined;

/**
 * TypeORM serves `decimal` columns as strings even though the DTOs declare
 * `number`; the Datagrid prints either as-is, and so must the CSV.
 */
export type DecimalCell = number | string | null;

export type WithDecimalCells<Row, Key extends keyof Row> = Omit<Row, Key> &
  Record<Key, DecimalCell>;

export interface CsvColumn<Row> {
  /** Must match the on-screen column label. */
  label: string;
  value: (row: Row) => CsvCellValue;
}

export interface CostStructureHistoryRow {
  createdAt: string | Date;
  changedByUser?: Pick<UserInterface, 'firstName' | 'lastName'>;
}

/** Shape of the Datagrid column definitions in a module's `auditColumns`. */
export interface AuditColumnLike<Row> {
  label: string;
  source: keyof Row & string;
  format?: (value: unknown) => string;
}

export interface BuildCostStructureHistoryCsvArgs<Row> {
  name: string;
  /** In on-screen order (Timestamp descending). */
  rows: Row[];
  /** In on-screen order. */
  columns: CsvColumn<Row>[];
  exportDate?: Date;
}

export interface CostStructureHistoryCsv {
  /** Without extension: react-admin's `downloadCSV` appends `.csv`. */
  filename: string;
  csv: string;
}

export const CHANGED_BY_CSV_COLUMN: CsvColumn<CostStructureHistoryRow> = {
  label: 'Name',
  value: row =>
    row.changedByUser
      ? `${row.changedByUser.firstName} ${row.changedByUser.lastName}`
      : null,
};

/** Matches the wall-clock rendering of the screen's `DateField`. */
export const TIMESTAMP_CSV_COLUMN: CsvColumn<CostStructureHistoryRow> = {
  label: 'Timestamp',
  value: row =>
    formatDateTime(row.createdAt, COST_STRUCTURE_HISTORY_TIMESTAMP_FORMAT),
};

/**
 * Mirrors how the Datagrid renders cost/toggle columns, so the file reads
 * like the screen: its `-` placeholder becomes an empty cell.
 */
export const auditColumnsToCsvColumns = <Row>(
  columns: AuditColumnLike<Row>[],
): CsvColumn<Row>[] =>
  columns.map(column => ({
    label: column.label,
    value: row => {
      const value = row[column.source];
      if (column.format) return column.format(value);
      return typeof value === 'number' || typeof value === 'string'
        ? value
        : null;
    },
  }));

const FILESYSTEM_UNSAFE_CHARACTERS = /[/\\:*?"<>|]/g;

export const sanitizeCsvFileNamePart = (name: string): string =>
  name.trim().replace(/\s+/g, '-').replace(FILESYSTEM_UNSAFE_CHARACTERS, '');

const CSV_NEEDS_QUOTING = /[",\r\n]/;

/** RFC-4180 quoting. */
export const escapeCsvCell = (value: CsvCellValue): string => {
  if (value === null || value === undefined) return '';

  const text = String(value);
  return CSV_NEEDS_QUOTING.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildCostStructureHistoryCsv = <Row>({
  name,
  rows,
  columns,
  exportDate = new Date(),
}: BuildCostStructureHistoryCsvArgs<Row>): CostStructureHistoryCsv => {
  const header = columns.map(column => escapeCsvCell(column.label));
  const body = rows.map(row =>
    columns.map(column => escapeCsvCell(column.value(row))),
  );
  const csv = [header, ...body].map(line => line.join(',')).join('\n');

  const date = formatDateTime(exportDate, 'yyyy-MM-dd');
  const filename = `cost-structure-history_${sanitizeCsvFileNamePart(
    name,
  )}_${date}`;

  return { filename, csv };
};
